/* Daps — international shipping messaging.
 *
 * Reads the zone resolved server-side by snippets/shipping-zone.liquid and
 * keeps the "distance to free shipping" line in sync with the buy box.
 *
 * Hard rule: this file NEVER touches the buy box. It does not modify
 * snippets/buy-module.liquid or assets/daps-buy.js, does not bind to the
 * add-to-cart button, and does not alter variant or selling-plan state.
 * It LISTENS — a MutationObserver on the aria-pressed attributes that
 * daps-buy.js already toggles — and writes only into its own nodes.
 *
 * Currency: thresholds are denominated in the shop currency (USD) because
 * that is what the Shopify delivery profile compares against. Markets may
 * show this visitor another currency, so every comparison is done in shop
 * currency via Shopify.currency.rate, and the gap is displayed back in the
 * visitor's own currency so it reconciles with their cart.
 *
 * No dependencies. Vanilla only.
 */
(function () {
  "use strict";

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  /* ---- currency -------------------------------------------------------- */

  /* Shopify.currency = { active: "EUR", rate: "0.92" } — present on every
     storefront via content_for_header. rate converts shop -> presentment. */
  function fxRate() {
    try {
      var r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return (isFinite(r) && r > 0) ? r : 1;
    } catch (e) { return 1; }
  }

  function activeCurrency(fallback) {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || fallback;
    } catch (e) { return fallback; }
  }

  /* presentment cents -> shop-currency units (what the delivery rule sees) */
  function toShopUnits(presentmentCents) {
    return (presentmentCents / 100) / fxRate();
  }

  /* shop-currency units -> presentment cents, rounded UP so we never
     understate how far the customer still has to go. */
  function toPresentmentCents(shopUnits) {
    return Math.ceil(shopUnits * fxRate() * 100);
  }

  function formatMoney(cents, iso) {
    var amount = cents / 100;
    try {
      /* "symbol", not "narrowSymbol": the threshold above this line is stated
         in USD, so a bare "$" on a CAD gap would read as the same currency
         and make the gap look bigger than the threshold. en + CAD -> "CA$". */
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency", currency: iso, currencyDisplay: "symbol"
      }).format(amount);
    } catch (e) {
      try {
        return new Intl.NumberFormat("en", { style: "currency", currency: iso }).format(amount);
      } catch (e2) {
        return "$" + amount.toFixed(2);
      }
    }
  }

  /* ---- buy-box reading (read-only) ------------------------------------- */

  /* The buy module already publishes its variants + selling-plan prices as a
     JSON island for daps-buy.js. We read it; we never write to it. */
  function readIsland(root) {
    var node = $(".buymod-data", root);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function selectedPack(root) {
    var el = $$("[data-pack]", root).filter(function (o) {
      return o.getAttribute("aria-pressed") === "true";
    })[0];
    return el ? el.getAttribute("data-pack") : null;
  }

  function selectedFlavor(root) {
    var el = $$("[data-flavor-btn]", root).filter(function (o) {
      return o.getAttribute("aria-pressed") === "true";
    })[0];
    return el ? el.getAttribute("data-flavor-btn") : null;
  }

  function isSubscribe(root) {
    var el = $$("[data-mode-btn]", root).filter(function (o) {
      return o.getAttribute("aria-pressed") === "true";
    })[0];
    return el ? el.getAttribute("data-mode-btn") === "subscribe" : false;
  }

  function variantFor(island, flavor, pack) {
    if (!island || !island.variants) return null;
    var vs = island.variants;
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      if (v.pack !== pack) continue;
      if (flavor && v.flavor && v.flavor !== flavor) continue;
      return v;
    }
    return null;
  }

  /* Quantity, as published by daps-buy.js on the module root. Falls back to
     the visible readout, then to 1, so this works even if the stepper is off. */
  function selectedQty(root) {
    var a = parseInt(root.getAttribute("data-qty"), 10);
    if (isFinite(a) && a > 0) return a;
    var el = $("[data-qty-val]", root);
    var b = el ? parseInt(el.textContent, 10) : NaN;
    return (isFinite(b) && b > 0) ? b : 1;
  }

  /* Unit price in presentment cents, honouring subscribe mode. */
  function unitPrice(island, root) {
    var pack = selectedPack(root);
    if (!pack) return null;
    var v = variantFor(island, selectedFlavor(root), pack);
    if (!v) return null;
    if (isSubscribe(root)) {
      var subs = (v.subs && v.subs.length) ? v.subs : (v.sub ? [v.sub] : []);
      if (subs.length) {
        /* match the frequency daps-buy.js currently shows, else first */
        var sel = $("[data-freq-opts] select", root);
        var label = sel ? sel.value : null;
        for (var i = 0; i < subs.length; i++) {
          if (label && subs[i].label === label) return subs[i].price;
        }
        return subs[0].price;
      }
    }
    return v.price;
  }

  /* What the customer's shipping is actually assessed on: the whole line. */
  function currentPrice(island, root) {
    var unit = unitPrice(island, root);
    if (unit == null) return null;
    return unit * selectedQty(root);
  }

  /* Available packs for the current flavour/mode, cheapest first. */
  function packOptions(island, root) {
    if (!island || !island.variants) return [];
    var flavor = selectedFlavor(root);
    var sub = isSubscribe(root);
    var seen = {};
    var opts = [];

    island.variants.forEach(function (v) {
      if (flavor && v.flavor && v.flavor !== flavor) return;
      if (!v.available) return;
      if (seen[v.pack]) return;
      seen[v.pack] = 1;
      var price = v.price;
      if (sub) {
        var subs = (v.subs && v.subs.length) ? v.subs : (v.sub ? [v.sub] : []);
        if (subs.length) price = subs[0].price;
      }
      opts.push({ pack: v.pack, price: price });
    });

    opts.sort(function (a, b) { return a.price - b.price; });
    return opts;
  }

  /* The next step has to be something the buy box can actually do. This is a
     single-pack selector with no quantity field, so "add another pack" is not
     an available action — switching pack is. Returns the cheapest pack that on
     its own clears the threshold, or null when none does (Canada/Europe, where
     even the biggest pack sits under it — we must not imply otherwise). */
  function packThatClears(island, root, thresholdPresentmentCents) {
    var opts = packOptions(island, root);
    if (!opts.length) return null;
    var current = selectedPack(root);
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].pack !== current && opts[i].price >= thresholdPresentmentCents) return opts[i].pack;
    }
    return null;
  }

  /* ---- rendering ------------------------------------------------------- */

  /* One component: label + progress track (+ a short secondary line).
     `els` is captured once by initBuybox — the block gets relocated above the
     add-to-cart button, so note-scoped re-lookups would come back empty. */
  function renderShip(note, els, island, root) {
    if (!els.label) return;

    var subFree = note.getAttribute("data-sub-free") === "1";
    var supported = note.getAttribute("data-supported") === "1";

    /* US + subscribe: subscriptions ship free with no minimum, and the trust
       row under the button already says so with a check. Nothing to chase and
       nothing to add — take the whole block out. */
    if (subFree && isSubscribe(root)) {
      note.hidden = true;
      return;
    }
    note.hidden = false;

    if (!supported) return;

    var threshold = parseFloat(note.getAttribute("data-threshold"));
    if (!isFinite(threshold) || threshold <= 0) return;

    var price = currentPrice(island, root);
    if (price == null) return;

    var iso = activeCurrency(note.getAttribute("data-currency") || "USD");
    var isDomestic = note.getAttribute("data-zone") === "domestic";

    var haveShopUnits = toShopUnits(price);
    var met = haveShopUnits >= threshold;
    var pct = Math.max(2, Math.min(100, Math.round((haveShopUnits / threshold) * 100)));

    if (els.track) els.track.hidden = false;
    if (els.fill) els.fill.style.width = (met ? 100 : pct) + "%";

    /* Deliberately no zone name: a visitor only ever sees their own zone's
       threshold, so naming it adds nothing and reads as clutter. The zone is
       still stated once in the announcement bar and the trust line. */
    if (met) {
      els.label.textContent = "\u2713 this order ships free.";
      els.label.classList.add("is-met");
    } else {
      els.label.textContent = "you're " + formatMoney(toPresentmentCents(threshold - haveShopUnits), iso) +
                              " from free shipping.";
      els.label.classList.remove("is-met");
    }

    /* Secondary line, one short sentence at most: the concrete next step when
       a single pack can actually reach the threshold, otherwise the flat rate
       they will pay (which is the more useful fact internationally, where no
       single pack clears it). */
    if (els.meta) {
      var line = "";
      if (!met) {
        /* Quantity is the most direct lever now that the stepper exists: how
           many of the pack they already chose would clear the threshold? Only
           suggest it if it is a sane number. */
        var unit = unitPrice(island, root);
        var qty = selectedQty(root);
        var needQty = (unit && unit > 0) ? Math.ceil(threshold / toShopUnits(unit)) : 0;
        if (needQty > qty && needQty <= 6) {
          /* In subscribe mode the threshold is met on EVERY renewal, which is
             the only way an international subscriber ever gets free shipping —
             a one-time add-on only covers the first order. Say so. */
          line = isSubscribe(root)
            ? "make it " + needQty + " so every order ships free"
            : "make it " + needQty + " to ship free";
        } else {
          var closer = packThatClears(island, root, toPresentmentCents(threshold));
          if (closer) line = "switch to the " + closer + " to ship free";
        }
      }
      /* Only while they still owe shipping. Once the threshold is met the
         label says it ships free, and quoting a flat rate next to that
         reads as a contradiction. */
      if (!line && !met && !isDomestic) {
        var rate = note.getAttribute("data-rate");
        if (rate) line = "$" + rate + " flat rate";
      }
      els.meta.textContent = line;
      els.meta.hidden = line === "";
    }
  }

  /* The sticky buy bar is painted by daps-buy.js, which hardcodes
     "N% off · free shipping" for subscriptions. That is US-only truth. We
     cannot edit that file (subscription checkout runs through it), so for
     zones without free subscription shipping we correct the text in place
     after it renders. Guarded so we never fight its render loop. */
  function correctStickyBar(note, root) {
    if (note.getAttribute("data-sub-free") === "1") return;
    var info = $("[data-buybar-info]", root);
    if (!info) return;
    var span = info.querySelector("span");
    if (!span) return;
    if (/free shipping/i.test(span.textContent)) {
      span.textContent = span.textContent.replace(/\s*·?\s*free shipping/i, "");
      if (!span.textContent.trim()) span.textContent = "subscribe & save";
    }
  }

  /* ---- wiring ---------------------------------------------------------- */

  function initBuybox(note) {
    /* The buy module root is a sibling/ancestor-adjacent node; find the
       nearest one in the same section. */
    var scope = note.closest("[data-sec], section, .shopify-section") || document;
    var root = $("[data-placement]", scope) || $(".buymod-data", scope);
    if (root && root.classList && root.classList.contains("buymod-data")) root = root.parentElement;
    if (!root) return;

    var island = readIsland(root);

    /* Captured once: the block is relocated below, so note-scoped lookups
       afterwards would find nothing. */
    var els = {
      label: $("[data-dintl-label]", note),
      track: $("[data-dintl-track]", note),
      fill:  $("[data-dintl-fill]", note),
      meta:  $("[data-dintl-meta]", note)
    };

    /* Move the whole block directly above the add-to-cart button, and put it
       INSIDE .plan__foot rather than before it: the foot carries the buy box's
       own horizontal inset, so sitting in it lines the label and the bar up
       with the button (and the pack tiles) at every breakpoint, with no
       hardcoded padding to keep in sync. Runtime DOM move — the buy-box markup
       on disk is untouched. */
    var foot = $(".plan__foot", root);
    var cta = foot ? $("[data-add]", foot) : null;
    if (foot && cta) {
      /* directly above the button, which leaves the quantity stepper above us */
      foot.insertBefore(note, cta);
    } else if (foot) {
      foot.insertBefore(note, foot.firstChild);
    } else {
      var opts = $(".plan__opts", root);
      if (opts && opts.parentElement) opts.parentElement.insertBefore(note, opts.nextSibling);
    }

    var tiles = $$("[data-pack]", root);

    var paint = function () {
      /* Both run unconditionally: renderShip decides for itself whether this
         zone has a threshold to show, and a country we cannot ship to still
         must not be told that its subscription ships free. */
      renderShip(note, els, island, root);
      correctStickyBar(note, root);
    };

    /* daps-buy.js communicates state purely through aria-pressed on the
       tiles/mode buttons, so observing those is enough — no events needed
       and no handler of theirs is replaced. */
    var obs = new MutationObserver(paint);
    tiles.concat($$("[data-mode-btn]", root), $$("[data-flavor-btn]", root)).forEach(function (el) {
      obs.observe(el, { attributes: true, attributeFilter: ["aria-pressed"] });
    });
    /* daps-buy.js mirrors the quantity onto the root on every render */
    obs.observe(root, { attributes: true, attributeFilter: ["data-qty"] });

    /* Frequency is a <select> that daps-buy.js builds; catch it by delegation. */
    root.addEventListener("change", function (e) {
      if (e.target && e.target.tagName === "SELECT") paint();
    }, { passive: true });

    paint();
    /* daps-buy.js applies its default pack/mode after its own init; repaint
       once on the next frame so we reflect the settled state. */
    if (window.requestAnimationFrame) requestAnimationFrame(paint);
    setTimeout(paint, 300);
  }

  /* Cart page: Liquid could not do the arithmetic when the cart currency is
     not the shop currency (no FX rate in Liquid). Do it here, exactly, using
     the same rate Shopify used for the line prices. */
  function initCart() {
    var el = $("[data-dintl-cart]");
    if (!el) return;
    if (el.getAttribute("data-same-currency") === "1") return;
    if (el.getAttribute("data-all-sub") === "1" && el.getAttribute("data-sub-free") === "1") return;

    var label = $("[data-dintl-cart-label]", el);
    if (!label) return;

    var threshold = parseFloat(el.getAttribute("data-threshold"));
    var totalPresentment = parseInt(el.getAttribute("data-cart-total"), 10);
    if (!isFinite(threshold) || threshold <= 0 || !isFinite(totalPresentment)) return;

    var iso = activeCurrency("USD");
    var haveShopUnits = toShopUnits(totalPresentment);

    if (haveShopUnits >= threshold) {
      label.textContent = "✓ this order ships free.";
      label.classList.add("is-met");
      return;
    }

    label.textContent = "you're " + formatMoney(toPresentmentCents(threshold - haveShopUnits), iso) +
                        " from free shipping.";

    if (!$(".dintl-prog__track", el)) {
      var pct = Math.min(100, Math.round((haveShopUnits / threshold) * 100));
      var track = document.createElement("div");
      track.className = "dintl-prog__track";
      var fill = document.createElement("span");
      fill.className = "dintl-prog__fill";
      fill.style.width = pct + "%";
      track.appendChild(fill);
      el.appendChild(track);
    }
  }

  function init() {
    $$("[data-dintl]").forEach(function (note) {
      if (note.classList.contains("dintl--buybox")) initBuybox(note);
    });
    initCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
